import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import moment from "moment";
import EditPaymentModal from "./EditPaymentModal";
import {
  updatePaymentBulk as onUpdatePaymentBulk,
} from "../../slices/thunks";
import { useDispatch } from "react-redux";

const PaymentAccordion = ({
  payments,
  settlementPrice,
  unitInfo,
  aggregated,
}) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [markAsPaidModalOpen, setMarkAsPaidModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [editForm, setEditForm] = useState({
    settlement_price: 0,
    vat_percentage: 19,
  });
  const dispatch = useDispatch();

  const remainingBalance = Math.max(
    payments
      .filter(
        (payment) => payment.cashed === null || parseFloat(payment.cashed) <= 0
      )
      .reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0),
    0
  );

  const calculatedSettlementPrice =
    (settlementPrice?.settled_price &&
      parseFloat(settlementPrice.settled_price)) ||
    Math.max(...payments.map((p) => parseFloat(p.settlement_price || 0)), 0);

  const handleManagePayments = (unit = null) => {
    if (unit) {
      const sortedPayments = [...unit.settlementPrice.payments].sort((a, b) => {
        const dateA = a.booking_date ? moment(a.booking_date) : moment(0);
        const dateB = b.booking_date ? moment(b.booking_date) : moment(0);
        return dateA.diff(dateB);
      });

      setSelectedUnit(unit);
      setEditForm({
        settlement_price:
          unit.settlementPrice.settled_price || calculatedSettlementPrice,
        vat_percentage:
          unit.settlementPrice.payments.length > 0
            ? parseInt(unit.settlementPrice.payments[0].vat_percentage || 19)
            : 19,
        payments: sortedPayments,
        unitInfo: unit.unitInfo,
      });
    } else {
      const sortedPayments = [...payments].sort((a, b) => {
        const dateA = a.booking_date ? moment(a.booking_date) : moment(0);
        const dateB = b.booking_date ? moment(b.booking_date) : moment(0);
        return dateA.diff(dateB);
      });

      setEditForm({
        settlement_price: calculatedSettlementPrice,
        vat_percentage:
          payments.length > 0 ? parseInt(payments[0].vat_percentage || 19) : 19,
        payments: sortedPayments,
        unitInfo: unitInfo,
      });
    }

    setEditModalOpen(true);
  };

  const handleSavePayments = async (updatedPayments, settledPrice, unit) => {
    try {
      updatedPayments.forEach((payment) => {
        if (payment.unitAmounts && payment.unitAmounts.length > 0) {
          payment.amount = payment.unitAmounts.reduce(
            (sum, unit) => sum + parseFloat(unit.amount || 0),
            0
          );
        }
      });

      const totalAmount = updatedPayments.reduce(
        (sum, payment) => sum + parseFloat(payment.amount || 0),
        0
      );

      const settlementPriceValue = parseFloat(
        settledPrice?.settled_price || settledPrice || 0
      );
      if (Math.abs(totalAmount - settlementPriceValue) > 0.01) {
        console.error(
          `Total payments (€${totalAmount.toFixed(
            2
          )}) must exactly match the settlement price (€${settlementPriceValue.toFixed(
            2
          )})`
        );
        return;
      }

      const originalPayments = selectedUnit
        ? selectedUnit.settlementPrice.payments
        : payments;

      const existingPaymentIds = originalPayments
        .filter((p) => p.id && !p.id.toString().startsWith("temp-"))
        .map((p) => p.id);

      const updatedPaymentIds = updatedPayments
        .filter((p) => p.id && !p.id.toString().startsWith("temp-"))
        .map((p) => p.id);

      const deletedPaymentIds = existingPaymentIds.filter(
        (id) => !updatedPaymentIds.includes(id)
      );

      if (
        aggregated &&
        !unit &&
        updatedPayments.some((p) => p.unitAmounts && p.unitAmounts.length > 0)
      ) {
        const allUnitPayments = {};
        const existingUnitPaymentMap = new Map();
        const unitPaymentsMap = new Map();

        updatedPayments.forEach((payment) => {
          if (payment.unitAmounts && payment.unitAmounts.length > 0) {
            let normalizedDate = payment.booking_date;
            if (normalizedDate) {
              normalizedDate = moment(normalizedDate).format("YYYY-MM-DD");
            }

            payment.unitAmounts.forEach((unitAmount) => {
              const unitId = unitAmount.unit_id;

              if (!unitPaymentsMap.has(unitId)) {
                unitPaymentsMap.set(unitId, []);
              }

              const keys = [
                `${payment.type}-${normalizedDate}-${unitId}`,
                `${payment.type}-${payment.booking_date}-${unitId}`,
                `${payment.type}-${unitId}`,
              ];

              let existingPaymentData = null;
              for (const key of keys) {
                if (existingUnitPaymentMap.has(key)) {
                  existingPaymentData = existingUnitPaymentMap.get(key);
                  break;
                }
              }

              unitPaymentsMap.get(unitId).push({
                id: existingPaymentData ? existingPaymentData.id : null,
                type: payment.type,
                amount: parseFloat(unitAmount.amount || 0),
                booking_date: payment.booking_date,
                vat_percentage: parseInt(payment.vat_percentage || 19),
                cashed: payment.cashed,
                cashed_date: payment.cashed_date,
              });
            });
          }
        });

        const updatePromises = [];

        for (const [unitId, unitPaymentsList] of unitPaymentsMap.entries()) {
          let unitSettlementPriceId = null;

          if (unitPaymentsList && contract.units) {
            const unitData = contract.units.find((u) => u.id == unitId);
            if (unitData && unitData.settlement_price) {
              unitSettlementPriceId = unitData.settlement_price.id;
            }
          }

          if (
            !unitSettlementPriceId &&
            unitPaymentsList.some((p) => p.settlement_price)
          ) {
            unitSettlementPriceId = unitPaymentsList.find(
              (p) => p.settlement_price
            ).settlement_price;
          }

          const unitExistingPayments = allUnitPayments[unitId] || [];
          const unitExistingIds = unitExistingPayments
            .filter((p) => p.id && !p.id.toString().startsWith("temp-"))
            .map((p) => p.id);

          const unitUpdatedIds = unitPaymentsList
            .filter((p) => p.id && !p.id.toString().startsWith("temp-"))
            .map((p) => p.id);

          const unitDeletedIds = unitExistingIds.filter(
            (id) => !unitUpdatedIds.includes(id)
          );

          const bulkUpdatePayload = {
            payments: unitPaymentsList,
            settlement_price_id: unitSettlementPriceId,
            deleted_payment_ids: unitDeletedIds,
            unit_id: unitId,
            unit_payments: true,
            force_update: true,
          };
          updatePromises.push(dispatch(onUpdatePaymentBulk(bulkUpdatePayload)));
        }

        await Promise.all(updatePromises);
      } else {
        const settlementPriceId = {
          id: settlementPrice.id,
          settlementPrice: settledPrice,
        };

        const formattedPayments = [];

        updatedPayments.forEach((payment) => {
          const isNewPayment =
            !payment.id || payment.id.toString().startsWith("temp-");

          if (payment.unitAmounts && payment.unitAmounts.length > 0) {
            payment.unitAmounts.forEach((unitAmount) => {
              formattedPayments.push({
                id: isNewPayment ? null : payment.id,
                type: payment.type,
                amount: parseFloat(unitAmount.amount || 0),
                booking_date: payment.booking_date,
                vat_percentage: parseInt(payment.vat_percentage || 19),
                cashed: payment.cashed,
                cashed_date: payment.cashed_date,
              });
            });
          } else {
            formattedPayments.push({
              id: isNewPayment ? null : payment.id,
              type: payment.type,
              amount: parseFloat(payment.amount || 0),
              booking_date: payment.booking_date,
              vat_percentage: parseInt(payment.vat_percentage || 19),
              cashed: payment.cashed,
              cashed_date: payment.cashed_date,
            });
          }
        });

        const bulkUpdatePayload = {
          payments: formattedPayments,
          settlement_price: settlementPriceId,
          deleted_payment_ids: deletedPaymentIds,
          unit_payments: true,
          force_update: true,
        };
        await dispatch(onUpdatePaymentBulk(bulkUpdatePayload));
      }

      setEditModalOpen(false);
      setSelectedUnit(null);
    } catch (error) {
      console.error("Error updating payments:", error);
    }
  };

  const checkPaymentStatus = (paymentDate, cashed) => {
    const today = moment();
    const paymentMoment = moment(paymentDate);
    const diffInDays = paymentMoment.diff(today, "days");

    if (cashed === null || parseFloat(cashed || 0) <= 0) {
      return diffInDays < 0 ? "red" : diffInDays <= 7 ? "orange" : "green";
    }

    return "green";
  };

  const isIndividualPaymentsView = /^\/contracts\/\d+$/.test(
    window.location.pathname
  );

  const cardTitle = unitInfo ? `Payments - Unit ${unitInfo.nr}` : "Payments";

  return (
    <Card className="border-0 shadow-none">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <span className="fw-bold fs-4 text-dark">{cardTitle}</span>
        <span className="text-muted">
          Balance: €{Intl.NumberFormat("en-US").format(remainingBalance)}
        </span>
      </CardHeader>

      <CardBody className="p-0">
        <div className="align-items-center p-3 justify-content-between d-flex">
          <div className="flex-shrink-0 w-100">
            <div className="text-muted d-flex justify-content-between align-items-center">
              <span>
                <span className="fw-semibold">
                  {
                    payments.filter(
                      (p) => p.cashed === null || parseFloat(p.cashed || 0) <= 0
                    ).length
                  }
                </span>{" "}
                of <span className="fw-semibold">{payments.length}</span>{" "}
                remaining
              </span>
              {!aggregated ?
              <Button
                outline
                color="primary"
                size="sm"
                onClick={() => handleManagePayments()}
              >
                <i className="ri-settings-line align-middle me-1"></i> Edit Payments
              </Button> : null}
            </div>
          </div>
        </div>

        <div style={{ maxHeight: "500px", overflow: "auto" }}>
          <ul className="list-group list-group-flush border-dashed px-3">
            {payments.map((payment, index) => {
              const paymentStatus = checkPaymentStatus(
                payment.booking_date,
                payment.cashed
              );
              const isPaid =
                payment.cashed !== null && parseFloat(payment.cashed || 0) > 0;

              return (
                <li className="list-group-item ps-0" key={payment.id || index}>
                  <div className="d-flex align-items-start">
                    <div className="form-check ps-0 flex-shrink-0">
                      <input
                        type="checkbox"
                        className="form-check-input ms-0"
                        id={`payment-${payment.id}`}
                        checked={isPaid}
                        disabled={isIndividualPaymentsView}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <label
                        className={`form-check-label mb-0 ps-2 ${
                          isPaid ? "text-muted" : ""
                        }`}
                        htmlFor={`payment-${payment.id}`}
                      >
                        {payment.type} - €
                        {Intl.NumberFormat("en-US").format(
                          parseFloat(payment.amount || 0)
                        )}
                      </label>
                      <p
                        className={`fs-12 mb-0 ${
                          paymentStatus === "red"
                            ? "text-danger"
                            : paymentStatus === "orange"
                            ? "text-warning"
                            : paymentStatus === "green" && !isPaid
                            ? "text-success"
                            : isPaid
                            ? "text-muted"
                            : ""
                        }`}
                      >
                        {paymentStatus === "red"
                          ? "Overdue"
                          : paymentStatus === "orange"
                          ? "Soon to be paid"
                          : paymentStatus === "green" && !isPaid
                          ? "Upcoming"
                          : isPaid
                          ? "Paid"
                          : ""}
                        {" - "}
                        {moment(payment.booking_date).format("DD MMM YYYY")}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </CardBody>

      {/* Edit Payment Modal - for managing all payments */}
      <EditPaymentModal
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen(false)}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={handleSavePayments}
      />

      {/* Mark as Paid Modal */}
      <Modal
        isOpen={markAsPaidModalOpen}
        toggle={() => setMarkAsPaidModalOpen(false)}
      >
        <ModalHeader toggle={() => setMarkAsPaidModalOpen(false)}>
          {selectedPayment &&
          selectedPayment.cashed !== null &&
          parseFloat(selectedPayment.cashed || 0) > 0
            ? "Mark Payment as Unpaid"
            : "Mark Payment as Paid"}
        </ModalHeader>
        <ModalBody>
          {selectedPayment && (
            <div className="mb-3">
              <h6 className="mb-3">Payment Details:</h6>
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-bold">Type:</span>
                <span>{selectedPayment.type}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-bold">Amount:</span>
                <span>
                  €
                  {Intl.NumberFormat("en-US").format(
                    parseFloat(selectedPayment.amount || 0)
                  )}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-bold">Date:</span>
                <span>
                  {moment(selectedPayment.booking_date).format("DD MMM YYYY")}
                </span>
              </div>
              {selectedPayment.cashed !== null &&
                parseFloat(selectedPayment.cashed || 0) > 0 && (
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fw-bold">Paid on:</span>
                    <span>
                      {moment(selectedPayment.cashed_date).format(
                        "DD MMM YYYY"
                      )}
                    </span>
                  </div>
                )}
              {unitInfo && (
                <div className="d-flex justify-content-between mb-2">
                  <span className="fw-bold">Unit:</span>
                  <span>{unitInfo.nr}</span>
                </div>
              )}
              <hr />
            </div>
          )}
          <p>
            {selectedPayment &&
            selectedPayment.cashed !== null &&
            parseFloat(selectedPayment.cashed || 0) > 0
              ? "Are you sure you want to mark this payment as unpaid?"
              : "Are you sure you want to mark this payment as paid?"}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setMarkAsPaidModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color={
              selectedPayment &&
              selectedPayment.cashed !== null &&
              parseFloat(selectedPayment.cashed || 0) > 0
                ? "danger"
                : "success"
            }
          >
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

export default PaymentAccordion;
